import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { fetchSimilarProducts } from '../api/api_products';
import ProductCompareCard from './ProductCompareCard';
import { useTranslation } from '../hooks/useTranslation';
import { purchasePricePerKgFromProduct } from '../utils/purchasePricePerKg';

const MAX_COMPARE = 9;
const SEARCH_DEBOUNCE_MS = 300;

const SORT_OPTIONS = [
  { id: 'relevance', label: 'Relevant', direction: null },
  // Score sorts: highest → lowest. CO2e kg/kg: lowest → highest (better climate first).
  { id: 'environment', label: 'Miljö', direction: 'desc' },
  { id: 'health', label: 'Hälsa', direction: 'desc' },
  { id: 'social', label: 'Rättvisa', direction: 'desc' },
  { id: 'total', label: 'Totalt poäng', direction: 'desc' },
  { id: 'co2e', label: 'CO2e kg/kg', direction: 'asc' },
];

const FilterChip = ({ active, disabled, onClick, children, ariaLabel }) => (
  <button
    type="button"
    disabled={disabled}
    aria-pressed={active}
    aria-label={ariaLabel || (typeof children === 'string' ? children : undefined)}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      active
        ? 'bg-blue-600 border-blue-600 text-white'
        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
    }`}
  >
    {children}
  </button>
);

/**
 * Modal: similar Menuwise-eligible products, compare up to 9, add under source row.
 */
const ProductCompareModal = ({
  isOpen,
  onClose,
  sourceProduct,
  onAddProduct,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const [addingId, setAddingId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sameBrand, setSameBrand] = useState(false);
  const [ecoOnly, setEcoOnly] = useState(false);
  const [sameCountry, setSameCountry] = useState(false);
  const [country, setCountry] = useState('');
  const [sort, setSort] = useState('relevance');
  const [filtersReady, setFiltersReady] = useState(false);
  const [listCanScrollMore, setListCanScrollMore] = useState(false);
  const [seedProduct, setSeedProduct] = useState(null);
  const similarListRef = useRef(null);

  const sourceId = sourceProduct?.id;
  const displaySource = useMemo(() => {
    if (!sourceProduct) return null;
    if (!seedProduct) return sourceProduct;
    return {
      ...sourceProduct,
      ...seedProduct,
      id: sourceProduct.id,
      image_url: seedProduct.image_url || sourceProduct.image_url,
    };
  }, [sourceProduct, seedProduct]);

  const seedBrand = displaySource?.brand || seedProduct?.brand || '';
  const seedCountryAlpha = (displaySource?.country_alpha2 || seedProduct?.country_alpha2 || '')
    .toString()
    .toLowerCase();
  const seedCountryName = displaySource?.country_name || seedProduct?.country_name || '';

  const countryOptions = useMemo(() => {
    const map = new Map();
    if (seedCountryAlpha) {
      map.set(seedCountryAlpha, seedCountryName || seedCountryAlpha.toUpperCase());
    }
    similar.forEach((p) => {
      const code = (p.country_alpha2 || '').toString().toLowerCase();
      if (!code || !/^[a-z]{2}$/.test(code)) return;
      if (!map.has(code)) {
        map.set(code, p.country_name || code.toUpperCase());
      }
    });
    return Array.from(map.entries())
      .map(([code, name]) => ({ value: code, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
  }, [similar, seedCountryAlpha, seedCountryName]);

  const countrySelectValue = useMemo(() => {
    if (sameCountry || !country) return null;
    return countryOptions.find((opt) => opt.value === country) || {
      value: country,
      label: country.toUpperCase(),
    };
  }, [sameCountry, country, countryOptions]);

  const countrySelectStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      minHeight: 28,
      height: 28,
      minWidth: '9.5rem',
      borderRadius: 9999,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.35)' : 'none',
      fontSize: 12,
      backgroundColor: state.isDisabled ? '#f9fafb' : '#fff',
      opacity: state.isDisabled ? 0.4 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: 28 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 28 }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
    clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (base) => ({ ...base, color: '#6b7280', fontSize: 12 }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: '#374151' }),
    menu: (base) => ({ ...base, fontSize: 12, zIndex: 60 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: 12,
      backgroundColor: state.isSelected
        ? '#2563eb'
        : state.isFocused
          ? '#eff6ff'
          : '#fff',
      color: state.isSelected ? '#fff' : '#374151',
    }),
  }), []);

  const filtersActive = Boolean(
    debouncedQ
    || sameBrand
    || ecoOnly
    || sameCountry
    || country
    || sort !== 'relevance'
  );

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedQ('');
    setSameBrand(false);
    setEcoOnly(false);
    setSameCountry(false);
    setCountry('');
    setSort('relevance');
  };

  const updateListScrollHint = () => {
    const el = similarListRef.current;
    if (!el) {
      setListCanScrollMore(false);
      return;
    }
    const hasOverflow = el.scrollHeight > el.clientHeight + 2;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    setListCanScrollMore(hasOverflow && !nearBottom);
  };

  useEffect(() => {
    if (loading || similar.length === 0) {
      setListCanScrollMore(false);
      return undefined;
    }
    const el = similarListRef.current;
    updateListScrollHint();
    if (!el) return undefined;
    el.addEventListener('scroll', updateListScrollHint, { passive: true });
    window.addEventListener('resize', updateListScrollHint);
    return () => {
      el.removeEventListener('scroll', updateListScrollHint);
      window.removeEventListener('resize', updateListScrollHint);
    };
  }, [loading, similar]);

  useEffect(() => {
    if (!isOpen) {
      setFiltersReady(false);
      return undefined;
    }
    setSearchInput('');
    setDebouncedQ('');
    setSameBrand(false);
    setEcoOnly(false);
    setSameCountry(false);
    setCountry('');
    setSort('relevance');
    setCompareIds(sourceId ? [sourceId] : []);
    setSimilar([]);
    setSeedProduct(null);
    setError(null);
    setFiltersReady(true);
    return undefined;
  }, [isOpen, sourceId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, isOpen]);

  useEffect(() => {
    if (!isOpen || !sourceId || !filtersReady) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetchSimilarProducts(sourceId, {
          q: debouncedQ,
          sameBrand,
          eco: ecoOnly,
          sameCountry,
          country: sameCountry ? undefined : country,
          sort,
          limit: 40,
        });
        if (cancelled) return;
        const payload = res.data;
        const rawList = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.products) ? payload.products : []);
        const list = rawList.filter((p) => p?.image_url);
        const seed = !Array.isArray(payload) && payload?.seed && typeof payload.seed === 'object'
          ? payload.seed
          : null;
        if (seed) setSeedProduct(seed);
        setSimilar(list);
        setCompareIds((prev) => {
          const keep = prev.filter((id) => id === sourceId || list.some((p) => p.id === id));
          return keep.length ? keep : [sourceId];
        });
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.error || t('Kunde inte hämta liknande produkter'));
          setSimilar([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  // t omitted on purpose — unstable i18n fn would refetch in a loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sourceId, filtersReady, debouncedQ, sameBrand, ecoOnly, sameCountry, country, sort]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA' || e.target?.tagName === 'SELECT') return;
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const byId = useMemo(() => {
    const map = new Map();
    if (displaySource) map.set(displaySource.id, displaySource);
    similar.forEach((p) => map.set(p.id, p));
    return map;
  }, [displaySource, similar]);

  const compareProducts = compareIds.map((id) => byId.get(id)).filter(Boolean);

  const toggleCompare = (productId) => {
    setCompareIds((prev) => {
      if (prev.includes(productId)) {
        if (productId === sourceId) return prev;
        return prev.filter((id) => id !== productId);
      }
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, productId];
    });
  };

  const handleAdd = async (product) => {
    if (!onAddProduct || !product?.id) return;
    setAddingId(product.id);
    try {
      await onAddProduct({
        ...product,
        purchase_price_per_kg: purchasePricePerKgFromProduct(product),
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || t('Kunde inte lägga till produkten'));
    } finally {
      setAddingId(null);
    }
  };

  const filterSummaryParts = [];
  if (sameBrand && seedBrand) filterSummaryParts.push(seedBrand);
  if (ecoOnly) filterSummaryParts.push(t('Ekologisk'));
  if (sameCountry && seedCountryAlpha) {
    filterSummaryParts.push(seedCountryName || seedCountryAlpha.toUpperCase());
  } else if (country) {
    const opt = countryOptions.find((c) => c.value === country);
    filterSummaryParts.push(opt?.label || country.toUpperCase());
  }
  if (debouncedQ) filterSummaryParts.push(`“${debouncedQ}”`);
  if (sort !== 'relevance') {
    const sortOpt = SORT_OPTIONS.find((s) => s.id === sort);
    const sortLabel = sortOpt?.label || sort;
    const dirHint =
      sortOpt?.direction === 'desc'
        ? t('högst först')
        : sortOpt?.direction === 'asc'
          ? t('lägst först')
          : null;
    filterSummaryParts.push(
      dirHint
        ? `${t('Sorterat')}: ${t(sortLabel)} (${dirHint})`
        : `${t('Sorterat')}: ${t(sortLabel)}`
    );
  }

  if (!isOpen || !sourceProduct || !displaySource) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-compare-title"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 id="product-compare-title" className="text-lg font-semibold text-gray-900">
              {t('Jämföre')}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('Jämför liknande produkter och lägg till under raden')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label={t('Stäng')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('Sök produktnamn')}
              className="flex-1 min-w-[12rem] max-w-sm px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-medium text-blue-700 hover:text-blue-900 px-2 py-1"
              >
                {t('Rensa filter')}
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterChip
              active={sameBrand}
              disabled={!seedBrand}
              ariaLabel={seedBrand ? `${t('Samma märke')} ${seedBrand}` : t('Samma märke')}
              onClick={() => setSameBrand((v) => !v)}
            >
              {seedBrand ? `${t('Samma märke')} · ${seedBrand}` : t('Samma märke')}
            </FilterChip>
            <FilterChip
              active={ecoOnly}
              ariaLabel={t('Ekologisk')}
              onClick={() => setEcoOnly((v) => !v)}
            >
              {t('Ekologisk')}
            </FilterChip>
            <FilterChip
              active={sameCountry}
              disabled={!seedCountryAlpha}
              ariaLabel={t('Samma land')}
              onClick={() => {
                setSameCountry((v) => {
                  const next = !v;
                  if (next) setCountry('');
                  return next;
                });
              }}
            >
              {seedCountryAlpha
                ? `${t('Samma land')} · ${seedCountryName || seedCountryAlpha.toUpperCase()}`
                : t('Samma land')}
            </FilterChip>
            <div className="inline-flex items-center min-w-[9.5rem]" aria-label={t('Land')}>
              <span className="sr-only">{t('Land')}</span>
              <Select
                value={countrySelectValue}
                onChange={(selected) => {
                  setSameCountry(false);
                  setCountry(selected?.value || '');
                }}
                options={countryOptions}
                isDisabled={sameCountry}
                isClearable
                isSearchable
                placeholder={t('Alla länder')}
                noOptionsMessage={() => t('Inga länder hittades')}
                className="react-select-container compare-country-select w-full"
                classNamePrefix="react-select"
                styles={countrySelectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                aria-label={t('Land')}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-gray-500">{t('Sortera')}:</span>
            <span className="relative inline-flex group">
              <button
                type="button"
                className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-400 text-[10px] font-bold text-gray-500 hover:border-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={t('Om sortering')}
                title={t('Om sortering')}
              >
                i
              </button>
              <span
                role="tooltip"
                className="pointer-events-none absolute left-1/2 bottom-full z-30 mb-1.5 w-64 -translate-x-1/2 rounded-md bg-gray-900 px-2.5 py-2 text-[11px] leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-4 after:border-transparent after:border-t-gray-900 after:content-['']"
              >
                {t('Relevant visar namn-liknande produkter först.')}
                {' '}
                {t('Miljö, Hälsa, Rättvisa och Totalt poäng sorterar högst poäng först.')}
                {' '}
                {t('CO2e kg/kg sorterar lägst värde först.')}
                {' '}
                ({t('högst först')}: ↑ · {t('lägst först')}: ↓)
              </span>
            </span>
            {SORT_OPTIONS.map((opt) => {
              const dirHint =
                opt.direction === 'desc'
                  ? t('högst först')
                  : opt.direction === 'asc'
                    ? t('lägst först')
                    : null;
              const hint = dirHint ? `${t(opt.label)} — ${dirHint}` : t(opt.label);
              const arrow = opt.direction === 'desc' ? '↑' : opt.direction === 'asc' ? '↓' : null;
              return (
                <FilterChip
                  key={opt.id}
                  active={sort === opt.id}
                  ariaLabel={`${t('Sortera')}: ${hint}`}
                  onClick={() => setSort(opt.id)}
                >
                  {arrow ? (
                    <span className="inline-flex items-center gap-1">
                      {t(opt.label)}
                      <span className="text-[10px] opacity-80" aria-hidden>{arrow}</span>
                    </span>
                  ) : (
                    t(opt.label)
                  )}
                </FilterChip>
              );
            })}
          </div>

          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-xs font-medium text-gray-600">
              {t('Liknande produkter')} ({similar.length}) — {t('Välj upp till')} {MAX_COMPARE - 1} {t('att jämföra')}
              {' '}({Math.max(0, compareIds.length - (sourceId ? 1 : 0))}/{MAX_COMPARE - 1})
            </p>
            {filterSummaryParts.length > 0 && (
              <p className="text-[11px] text-gray-500">
                {t('Filter')}: {filterSummaryParts.join(' · ')}
              </p>
            )}
          </div>

          {loading && <p className="text-sm text-gray-500">{t('Laddar')}...</p>}
          {!loading && !error && similar.length === 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
              <span>
                {filtersActive
                  ? t('Inga träffar med dessa filter')
                  : t('Inga liknande produkter hittades')}
              </span>
              {filtersActive && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs font-medium text-blue-700 hover:text-blue-900"
                >
                  {t('Rensa filter')}
                </button>
              )}
            </div>
          )}
          {!loading && similar.length > 0 && (
            <div className="relative">
              <div
                ref={similarListRef}
                className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-50/80 p-2 pr-1"
                style={{ scrollbarGutter: 'stable' }}
                aria-label={t('Liknande produkter')}
              >
                {similar.map((p) => {
                  const selected = compareIds.includes(p.id);
                  const atCap = !selected && compareIds.length >= MAX_COMPARE;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      disabled={atCap}
                      onClick={() => toggleCompare(p.id)}
                      className={`flex items-center gap-2 text-left text-xs px-2 py-1.5 rounded-lg border w-full min-w-0 ${
                        selected
                          ? 'bg-blue-50 border-blue-300 text-blue-800'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                      } disabled:opacity-40`}
                      title={p.name}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          className="w-8 h-8 object-contain rounded border border-gray-100 bg-gray-50 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded border border-gray-100 bg-gray-50 flex-shrink-0" />
                      )}
                      <span className="truncate min-w-0">
                        {selected ? '✓ ' : ''}{p.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {listCanScrollMore && (
                <button
                  type="button"
                  className="absolute inset-x-0 bottom-0 h-12 rounded-b-lg flex items-end justify-center pb-1.5 z-10 cursor-pointer border-0"
                  style={{
                    background: 'linear-gradient(to top, rgba(249,250,251,0.98) 35%, rgba(249,250,251,0))',
                  }}
                  aria-label={t('Fler produkter')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const el = similarListRef.current;
                    if (!el) return;
                    el.scrollBy({ top: Math.max(80, el.clientHeight * 0.7), behavior: 'smooth' });
                  }}
                >
                  <span className="text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-full px-2.5 py-0.5 shadow-sm pointer-events-none">
                    ↓ {t('Fler produkter')}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {error && (
            <p className="text-sm text-red-600 mb-3">{error}</p>
          )}
          {compareIds.length >= MAX_COMPARE && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mb-3">
              {t('Jämförelsen är full')} — {t('Ta bort från jämförelse')} {t('för att lägga till en annan')}
            </p>
          )}
          <div className="grid grid-cols-3 gap-4">
            {compareProducts.map((p) => (
              <ProductCompareCard
                key={`compare-${p.id}`}
                product={p}
                isCurrent={p.id === sourceId}
                onAdd={handleAdd}
                onRemove={(product) => toggleCompare(product.id)}
                adding={addingId === p.id}
                t={t}
              />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductCompareModal;
